import { FileSpreadsheet, Lock, Presentation } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useFileDownload } from '~/hooks/use-file-download';
import { cn } from '~/utils/cn';

import {
  downloadExperimentDidTrendCsv,
  downloadExperimentReadoutDeck,
} from '../../services/analytics.service';

interface Props {
  experimentId: string;
  /**
   * False until the Day 28 checkpoint passes — the deck and DiD trend aren't
   * computed before the post-period completes, so the buttons stay disabled.
   */
  isAvailable: boolean;
}

/**
 * Download actions for the 28-day results readout (HTML deck + DiD trend CSV),
 * rendered inside the Day 28 timeline row. Both run through the shared download
 * toast, which owns progress, errors and retry.
 */
export function ExperimentReadoutDownloads({ experimentId, isAvailable }: Props) {
  const { t } = useTranslation('experiments');
  const { start, isDownloading } = useFileDownload();

  const deckKey = `readout-deck-${experimentId}`;
  const csvKey = `did-trend-${experimentId}`;

  const strip = (
    // Tinted action strip so the cluster reads as the row's payoff rather than
    // two flat buttons floating under body text. Muted while unavailable — we
    // don't advertise a brand-gradient CTA the user can't actually use yet.
    <div
      className={cn(
        'flex w-fit flex-col gap-2.5 rounded-lg border p-3',
        isAvailable ? 'border-primary/20 bg-primary/5' : 'bg-muted/30 border-dashed',
      )}
    >
      <p
        className={cn(
          'flex items-center gap-1.5 text-xs font-semibold',
          isAvailable ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        {!isAvailable && <Lock aria-hidden="true" className="h-3 w-3" />}
        {isAvailable
          ? t('detail.readout.title', 'Your 28-day readout is ready')
          : t('detail.readout.titlePending', '28-day readout')}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {/* Primary: the deck is the customer-shareable headline artefact. */}
        <Button
          type="button"
          disabled={!isAvailable}
          isLoading={isDownloading(deckKey)}
          className={cn(isAvailable ? 'cursor-pointer' : 'cursor-not-allowed')}
          onClick={() => {
            start({
              key: deckKey,
              label: t('detail.readout.deckLabel', 'Results Readout deck'),
              run: () => downloadExperimentReadoutDeck(experimentId),
              errorFallback: t(
                'detail.readout.deckError',
                'Couldn’t download the readout deck. Try again.',
              ),
            });
          }}
        >
          <Presentation aria-hidden="true" />
          {t('detail.readout.deck', 'Readout Deck')}
        </Button>

        {/* Secondary: raw weekly DiD data, for the analytics team. */}
        <Button
          type="button"
          variant="outline"
          disabled={!isAvailable}
          isLoading={isDownloading(csvKey)}
          className={cn(
            isAvailable
              ? 'border-primary/40 text-primary hover:text-primary cursor-pointer bg-transparent'
              : 'cursor-not-allowed bg-transparent',
          )}
          onClick={() => {
            start({
              key: csvKey,
              label: t('detail.readout.csvLabel', 'DiD trend CSV'),
              run: () => downloadExperimentDidTrendCsv(experimentId),
              errorFallback: t(
                'detail.readout.csvError',
                'Couldn’t download the DiD trend CSV. Try again.',
              ),
            });
          }}
        >
          <FileSpreadsheet aria-hidden="true" />
          {t('detail.readout.csv', 'DiD Trend CSV')}
        </Button>
      </div>
    </div>
  );

  if (isAvailable) return strip;

  // Disabled buttons swallow pointer events, so the tooltip wraps the strip
  // rather than the buttons themselves to stay reachable on hover.
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <div className="w-fit">{strip}</div>
      </TooltipTrigger>
      <TooltipContent>
        {t('detail.readout.notReady', 'Available once the Day 28 readout completes.')}
      </TooltipContent>
    </Tooltip>
  );
}
